<script setup lang="ts">
interface ProcessStep {
  number: number;
  title: string;
  description: string;
  icon: string;
}

const steps: ProcessStep[] = [
  {
    number: 1,
    title: "Talk",
    description:
      "Tell us what's not working – internet, security, IT or all of the above.",
    icon: "lucide:message-circle",
  },
  {
    number: 2,
    title: "Plan",
    description:
      "We audit your setup and propose a practical plan that fits your budget.",
    icon: "lucide:clipboard-list",
  },
  {
    number: 3,
    title: "Deploy",
    description: "We manage the switch to ensure minimal disruption and maximum uptime.",
    icon: "lucide:rocket",
  },
];
</script>

<template>
  <section class="bg-neutral-50 py-20 lg:py-24">
    <div class="mx-auto max-w-7xl px-8">
      <!-- Section header -->
      <div class="text-center">
        <p class="text-sm font-semibold uppercase tracking-wider text-primary-400">
          How It Works
        </p>
        <h2
          class="mt-2 text-3xl font-extrabold tracking-tight text-slate-900 sm:text-4xl"
        >
          What happens when you get in touch?
        </h2>
      </div>

      <!-- Process steps -->
      <div class="relative mt-16">
        <!-- Connection line (desktop only) -->
        <div
          class="absolute left-0 right-0 top-24 hidden h-0.5 bg-neutral-200 lg:block"
          aria-hidden="true"
        ></div>

        <div class="grid gap-8 lg:grid-cols-3">
          <div
            v-for="step in steps"
            :key="step.number"
            class="group relative flex flex-col rounded-2xl border border-neutral-200 bg-white p-8 text-center shadow-xs transition-all duration-300 hover:-translate-y-1"
          >
            <!-- Step number with icon -->
            <div class="relative mx-auto mb-6">
              <!-- Number circle -->
              <div
                class="flex h-16 w-16 items-center justify-center rounded-full bg-primary-400 text-white shadow-lg transition-transform duration-300 group-hover:scale-110"
              >
                <span class="text-2xl font-extrabold">{{ step.number }}</span>
              </div>
              <!-- Icon badge -->
              <div
                class="absolute -bottom-1 -right-1 flex h-8 w-8 items-center justify-center rounded-full bg-mw-accent-500 text-white shadow-md"
              >
                <Icon :name="step.icon" size="16" />
              </div>
            </div>

            <!-- Content -->
            <h3 class="text-2xl font-extrabold text-dark">
              {{ step.title }}
            </h3>
            <p class="mt-3 flex-1 text-lg leading-7 text-neutral-600">
              {{ step.description }}
            </p>

            <!-- Bottom accent line -->
            <div
              class="absolute inset-x-0 bottom-0 h-1 origin-left scale-x-0 rounded-b-2xl bg-primary-400 transition-transform duration-300 group-hover:scale-x-100"
              aria-hidden="true"
            ></div>
          </div>
        </div>
      </div>

      <!-- Reassurance note -->
      <div
        class="mt-16 rounded-2xl border border-neutral-200 bg-white p-6 shadow-xs lg:p-8"
      >
        <div
          class="mx-auto flex max-w-4xl flex-col items-center justify-center gap-4 text-center sm:flex-row sm:text-left"
        >
          <Icon
            name="lucide:shield-check"
            size="48"
            class="shrink-0 text-mw-accent-500"
          />
          <p class="text-lg leading-7 text-neutral-600">
            <span class="font-extrabold text-slate-900">Worried about downtime?</span>
            We run parallel systems during migration so your business never stops.
          </p>
        </div>
      </div>
    </div>
  </section>
</template>
